import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  signal,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import {
  ActivatedRoute,
  ParamMap,
  Router,
  RouterModule
} from '@angular/router';
import { NbAccessChecker } from '@nebular/security';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { Application } from '~app/core/models/application.model';
import { routeAnimations } from '~core/core.module';
import { TableCanDeactivate } from '~core/utils/can-deactivate/table-can-deactivate';
import { ApplicationService } from '../application.service';
import { MetadataDefinitionTabComponent } from '../metadata-definition-tab/metadata-definition-tab.component';
import { MetadataSetsComponent } from '../metadata-set-tab/metadata-set-tab.component';

interface AppTab {
  link: string;
  label: string;
  icon: string;
  hidden: boolean;
  data?: {
    permission: string;
    resource: string;
    appRoles?: string[];
  };
}

@Component({
  selector: 'idl-application-details',
  templateUrl: './application-details.component.html',
  styleUrls: ['./application-details.component.scss'],
  animations: [routeAnimations],
  standalone: true,
  imports: [
    AsyncPipe,
    RouterModule,
    MatTabsModule,
    MatIconModule,
    MatSelectModule,
    TranslateModule
    // ... other modules used in the template
  ]
})
export class ApplicationDetailsComponent extends TableCanDeactivate
  implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private readonly _changeDetectorRef = inject(ChangeDetectorRef);
  private readonly _router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly _accessChecker = inject(NbAccessChecker);
  private readonly _applicationService = inject(ApplicationService);

  animationReady = false;

  application$: Observable<Application> = this.route.paramMap.pipe(
    switchMap((params: ParamMap) =>
      this._applicationService.getApp(+params.get('id'))
    ),
    shareReplay(1)
  );

  private readonly baseTabs: AppTab[] = [
    {
      link: 'edit',
      label: 'idl.menu.application.edit',
      icon: 'apps',
      hidden: false,
      data: {
        permission: 'edit',
        resource: 'application',
        appRoles: ['Manager', 'Developer']
      }
    },
    {
      link: 'email',
      label: 'idl.menu.application.emails',
      icon: 'email',
      hidden: false,
      data: {
        permission: 'edit',
        resource: 'application_email_templates',
        appRoles: ['Manager']
      }
    },
    {
      link: 'users',
      label: 'idl.menu.application.users',
      icon: 'people',
      hidden: false,
      data: {
        permission: 'edit',
        resource: 'application_users',
        appRoles: ['Manager']
      }
    },
    {
      link: 'versions',
      label: 'idl.menu.application.versions',
      icon: 'list',
      hidden: false,
      data: {
        permission: 'edit',
        resource: 'application_versions',
        appRoles: ['Developer']
      }
    },
    {
      link: 'metadata',
      label: 'idl.menu.application.metadata',
      icon: 'settings_applications',
      hidden: false,
      data: {
        permission: 'edit',
        resource: 'application_metadata',
        appRoles: ['Developer']
      }
    },
    {
      link: 'metadatasets',
      label: 'idl.menu.application.metadatasets',
      icon: 'view_list',
      hidden: false,
      data: {
        permission: 'edit',
        resource: 'application_metadata',
        appRoles: ['Developer']
      }
    },
    {
      link: 'licenses',
      label: 'idl.menu.application.licenses',
      icon: 'description',
      hidden: false,
      data: {
        permission: 'edit',
        resource: 'application_licenses',
        appRoles: ['Manager']
      }
    }
  ];

  appTabs = signal<AppTab[]>([...this.baseTabs]);

  // Computed signal for active tab
  activeTab = computed(() => {
    const currentUrl = this._router.url;
    return this.appTabs().find(tab => currentUrl.includes(tab.link));
  });

  mappTabs: MatTabGroup;
  @ViewChildren(MatTabGroup) childrenComponent: QueryList<MatTabGroup>;

  @ViewChild(MetadataDefinitionTabComponent, { static: false }) metadataDef;
  @ViewChild(MetadataSetsComponent, { static: false }) metadataSets;

  activeEdits = 0;

  ngOnInit() {
    this.application$.pipe(takeUntil(this.destroy$)).subscribe(application => {
      this.updateTabPermissions(application);
    });
  }

  ngAfterViewInit() {
    // Delay enabling animations until after initial rendering
    setTimeout(() => {
      this.animationReady = true;
      this._changeDetectorRef.detectChanges();
    }, 100);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onActiveEdit(active: number) {
    this.activeEdits = active;
  }

  prepareRoute(outlet) {
    return outlet && outlet.isActivated
      ? outlet.activatedRoute.routeConfig.path
      : '';
  }

  private updateTabPermissions(application: Application) {
    // Create a new tabs array to avoid mutating the original
    const updatedTabs = [...this.baseTabs];

    // For each tab, check permission and set the hidden flag
    updatedTabs.forEach((tab, index) => {
      let hasRole = true;
      if (tab.data?.appRoles) {
        // Check if the user has any of the required roles for this tab
        hasRole = application?.currentUserRoles?.some(role =>
          tab.data.appRoles.includes(role)
        );
      }

      if (tab.data?.permission && tab.data?.resource) {
        this._accessChecker
          .isGranted(tab.data.permission, tab.data.resource)
          .subscribe(granted => {
            // Create a new array to trigger change detection properly
            const currentTabs = this.appTabs();
            const newTabs = [...currentTabs];
            newTabs[index] = {
              ...newTabs[index],
              hidden: !granted && !hasRole // Hide if permission not granted and no role
            };
            this.appTabs.set(newTabs);
          });
      } else {
        // If no specific permission is set, just check roles
        const currentTabs = this.appTabs();
        const newTabs = [...currentTabs];
        newTabs[index] = {
          ...newTabs[index],
          hidden: !hasRole // Hide if no role
        };
        this.appTabs.set(newTabs);
      }
    });
  }
}
